import React, { PropsWithChildren } from 'react';
import { OfferModalProps } from '../../services/exports/Interfaces';
import CustomModal from '../../components-library/CustomModal';
import { MODAL_SIZES, OfferKindEnum } from '../../services/exports/Constants';
import useAppMethods from '../../hooks/utility/useAppMethods';
import CloseCircleIcon from '../../assets/icons/CloseCircleIcon';
import useOfferModalTranslation from './useOfferModalTranslation';
import OfferProducts from './OfferProducts';
import useMobileApp from '../../hooks/mobile-app/useMobileApp';
import useScreenType from '../../hooks/utility/useScreenType';
import { useTranslation } from 'react-i18next';
import useOffersStuff from '../../features/offers/hooks/useOffersStuff';

const OFFER_KIND_WITH_QUALIFYING_ITEMS: OfferKindEnum[] = [
  OfferKindEnum.PercentageOffByProducts,
  OfferKindEnum.FreeItemByProducts,
  OfferKindEnum.Bogo,
  OfferKindEnum.BogoHalfPrice,
  OfferKindEnum.AmountOffByProducts,
] as const;

const OFFER_KIND_WITH_REWARDS_ITEMS: OfferKindEnum[] = [
  OfferKindEnum.FreeItemByProducts,
  OfferKindEnum.FreeItemByMinSpendValue,
] as const;

const OFFER_KIND_WITH_QI_DESC: OfferKindEnum[] = [
  OfferKindEnum.FreeItemByProducts,
  OfferKindEnum.Bogo,
  OfferKindEnum.BogoHalfPrice,
] as const;

const OFFER_KIND_WITH_RW_ADD_BTN: OfferKindEnum[] = [OfferKindEnum.FreeItemByMinSpendValue] as const;

const OfferModal = (props: OfferModalProps) => {
  const { appliedOffers, unlockedOffers } = useOffersStuff();
  const { toggleOfferModal } = useAppMethods();
  const toggleModal = () => toggleOfferModal({ openModal: false });
  const { getTitleByOffer, getDescriptionByOffer } = useOfferModalTranslation(props.offer);
  const onClose = () => {
    if (props.onClose) {
      return props.onClose();
    }

    toggleModal();
  };
  const qualifyingProducts =
    OFFER_KIND_WITH_QUALIFYING_ITEMS.includes(props.offer.kind) && props.offer.details.products;
  const rewardsProducts = OFFER_KIND_WITH_REWARDS_ITEMS.includes(props.offer.kind) && props.offer.details.free_products;
  const { t } = useTranslation(null, { keyPrefix: 'Modals:OfferModal' });
  const qiDescription =
    OFFER_KIND_WITH_QI_DESC.includes(props.offer.kind) && t(`offers.${props.offer.kind}.qualifying_items_description`);
  const rewardDescription = t(`offers.${props.offer.kind}.reward_items_description`);
  const hasRewardItemsAddBtn =
    OFFER_KIND_WITH_RW_ADD_BTN.includes(props.offer.kind) &&
    !appliedOffers?.length &&
    unlockedOffers.some((o) => o.id === props.offer.id);

  return (
    <WithWrapper>
      <div className="flex flex-col p-lg space-y-lg">
        <div className="flex items-center">
          <h3 className="text-md">{getTitleByOffer(props.offer)}</h3>
          <button className="ml-auto" onClick={onClose}>
            <CloseCircleIcon />
          </button>
        </div>
        <p className="text-brand-text-secondary">{getDescriptionByOffer(props.offer)}</p>
        {qualifyingProducts && (
          <OfferProducts
            offer={props.offer}
            products={qualifyingProducts}
            hasAddBtn
            title={t('qualifying_items_title')}
            description={qiDescription}
          />
        )}
        {rewardsProducts && (
          <OfferProducts
            offer={props.offer}
            products={rewardsProducts}
            title={t('reward_items_title')}
            description={rewardDescription}
            hasAddBtn={hasRewardItemsAddBtn}
          />
        )}
      </div>
    </WithWrapper>
  );
};

const WithWrapper = ({ children }: PropsWithChildren) => {
  const { isMobileApp } = useMobileApp();
  const { toggleOfferModal } = useAppMethods();
  const { isDesktop } = useScreenType();
  const toggleModal = () => toggleOfferModal({ openModal: false });

  if (isMobileApp) {
    return children;
  }

  return (
    <CustomModal
      open
      position="center"
      zIndex={1300}
      size={MODAL_SIZES.LG}
      onClose={toggleModal}
      fullScreen={!isDesktop}
    >
      {children}
    </CustomModal>
  );
};

export default OfferModal;
